'use client';

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { useMutation } from '@apollo/client/react';
import Hyperbeam from '@hyperbeam/web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'use-intl';

import {
  ADD_HYPERBEAM_SESSION,
  REMOVE_HYPERBEAM_SESSION,
  HyperbeamSession,
} from '@/survey-graphql';

import {
  browserSessionAtom,
  setIntentionalUtExit,
} from './project-usability-browser-session.atoms';
import {
  createHyperbeamSession,
  checkIframeEmbeddabilityOnce,
  navigateHyperbeamTabs,
} from './project-usability-hyperbeam.helpers';

type HyperbeamInstance = Awaited<ReturnType<typeof Hyperbeam>>;

interface UseIframeEmbeddabilityCheckReturn {
  canEmbed: boolean | null;
  isChecking: boolean;
}

/**
 * Hook to check if URL can be embedded in iframe via server-side API.
 * Returns null while checking, then true/false based on server response.
 */
export function useIframeEmbeddabilityCheck(
  url: string | null
): UseIframeEmbeddabilityCheckReturn {
  const [lastUrl, setLastUrl] = useState(url);
  const [isChecking, setIsChecking] = useState<boolean>(!!url);
  const [canEmbed, setCanEmbed] = useState<boolean | null>(null);
  if (lastUrl !== url) {
    setLastUrl(url);
    setCanEmbed(null);
    setIsChecking(!!url);
  }

  useEffect(
    () => checkIframeEmbeddabilityOnce({ url, setCanEmbed, setIsChecking }),
    [url]
  );

  return { canEmbed, isChecking };
}

interface UseHyperbeamInstanceProps {
  contentUrl: string | null;
  containerElement: HTMLDivElement | null;
}

interface UseHyperbeamInstanceReturn {
  hyperbeamInstance: HyperbeamInstance | null;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
}

/**
 * Manages Hyperbeam session for usability testing.
 *
 * Session lifecycle:
 * - Checks atom for existing session first
 * - Creates session ONCE if none exists
 * - Navigates to new URLs using tabs API (no recreation)
 * - Session persists in global atom across component re-renders
 */
export function useHyperbeamInstance({
  contentUrl,
  containerElement,
}: UseHyperbeamInstanceProps): UseHyperbeamInstanceReturn {
  const tErrors = useTranslations('Errors');

  const isInitializingRef = useRef(false);
  const currentUrlRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createSessionMutation] = useMutation(ADD_HYPERBEAM_SESSION);
  const [removeSessionMutation] = useMutation(REMOVE_HYPERBEAM_SESSION);

  const [browserSession, setBrowserSession] = useAtom(browserSessionAtom);

  const onRemoveSession = useEffectEvent(async (sessionId: string) => {
    try {
      await removeSessionMutation({
        variables: { input: { sessionId } },
      });
    } catch {
      // Silent failure - session may already be closed
    }
  });

  const onCreateSession = useEffectEvent(
    async (startUrl: string): Promise<HyperbeamSession> => {
      const { data, error } = await createSessionMutation({
        variables: { input: { startUrl } },
      });

      if (error) {
        throw new Error(error.message || 'Failed to create Hyperbeam session');
      }

      if (!data?.addHyperbeamSession) {
        throw new Error('No data returned from createHyperbeamSession');
      }

      return data.addHyperbeamSession;
    }
  );

  useEffect(
    () =>
      createHyperbeamSession({
        browserSession,
        containerElement,
        contentUrl,
        isInitializingRef,
        currentUrlRef,
        setIsLoading,
        setError,
        setBrowserSession,
        onCreateSession,
        onRemoveSession,
        errorMessage: tErrors('browserSessionFailed'),
      }),
    [browserSession, containerElement, contentUrl, setBrowserSession, tErrors]
  );

  // Same URL still triggers tabs.update so the page reloads when the user
  // clicks the same button again.
  useEffect(
    () =>
      navigateHyperbeamTabs({
        contentUrl,
        instance: browserSession.instance,
        currentUrlRef,
      }),
    [contentUrl, browserSession.instance]
  );

  const existingInstance = browserSession.instance;
  const existingSessionId = browserSession.sessionId;
  const hasExistingInstance = !!existingInstance;

  return {
    hyperbeamInstance: existingInstance,
    isLoading: hasExistingInstance ? false : isLoading,
    error,
    sessionId: existingSessionId,
  };
}

const HYPERBEAM_BACK_TRAP_STATE = 'hyperbeam-back-trap';
// Hyperbeam goBack RPC + server-side page-load commit can exceed several hundred ms under load.
const HYPERBEAM_NAV_DETECTION_TIMEOUT_MS = 1500;
const HYPERBEAM_NAV_POLL_INTERVAL_MS = 100;

function isTrapState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'trap' in state &&
    state.trap === HYPERBEAM_BACK_TRAP_STATE
  );
}

function pushTrapEntry(): void {
  window.history.pushState({ trap: HYPERBEAM_BACK_TRAP_STATE }, '');
}

async function getActiveTabUrl(
  hb: HyperbeamInstance
): Promise<string | undefined> {
  try {
    const tabs = await hb.tabs.query({ active: true });
    return tabs[0]?.url;
  } catch {
    return undefined;
  }
}

/** Forward browser Back to Hyperbeam's tabs.goBack(); confirm exit when at the start of its history. */
export function useHyperbeamBackButtonTrap(
  hyperbeamInstance: HyperbeamInstance | null
): void {
  const tErrors = useTranslations('Errors');

  useEffect(() => {
    if (!hyperbeamInstance) return;
    const hb = hyperbeamInstance;

    let exiting = false;
    let cancelled = false;
    let confirmInFlight = false;
    let detectionInFlight = false;
    let latestTabUrl: string | undefined;
    let tabListenerActive = true;

    setIntentionalUtExit(false);
    pushTrapEntry();

    const onTabUpdated = (
      _tabId: number,
      changeInfo: { url?: string | undefined }
    ): void => {
      if (changeInfo.url) latestTabUrl = changeInfo.url;
    };

    void (async () => {
      try {
        await hb.tabs.onUpdated.addListener(onTabUpdated);
      } catch {
        return;
      }
      if (!tabListenerActive) {
        safeRemoveTabListener();
        return;
      }
      const url = await getActiveTabUrl(hb);
      if (url !== undefined && latestTabUrl === undefined) latestTabUrl = url;
    })();

    const exitMessage = tErrors('exitUsabilityTest');

    function confirmExit(): boolean {
      if (exiting || cancelled || confirmInFlight) return false;
      // Guard against duplicate prompts when popstates queue during the detection window.
      confirmInFlight = true;
      const proceed = window.confirm(exitMessage);
      confirmInFlight = false;
      return proceed;
    }

    async function detectGoBackResult(): Promise<'navigated' | 'exhausted'> {
      const beforeUrl = latestTabUrl ?? (await getActiveTabUrl(hb));
      if (beforeUrl === undefined) return 'exhausted';

      // Event-driven only: poll latestTabUrl which is fed by tabs.onUpdated. Avoids hammering
      // tabs.query during the detection window — the SDK's RPC channel gets overwhelmed on
      // rapid back clicks and rejects with TimedOutError.
      const startTime = Date.now();
      while (Date.now() - startTime < HYPERBEAM_NAV_DETECTION_TIMEOUT_MS) {
        if (cancelled || exiting) return 'navigated';
        if (latestTabUrl !== undefined && latestTabUrl !== beforeUrl) {
          return 'navigated';
        }
        await new Promise((resolve) =>
          setTimeout(resolve, HYPERBEAM_NAV_POLL_INTERVAL_MS)
        );
      }

      return 'exhausted';
    }

    function safeGoBack(): void {
      try {
        hb.tabs.goBack().catch(() => undefined);
      } catch {
        // Ignore — instance may be torn down between popstate firing and our handler running.
      }
    }

    function safeRemoveTabListener(): void {
      try {
        hb.tabs.onUpdated.removeListener(onTabUpdated).catch(() => undefined);
      } catch {
        // Ignore — instance may already be destroyed when cleanup runs.
      }
    }

    const handlePopState = (): void => {
      if (exiting || cancelled) return;

      // Re-arm sentinel synchronously before any await so a fast follow-up back press can't race past the trap.
      pushTrapEntry();

      // Hyperbeam queues goBack server-side, so fire on every popstate; detection runs once at a time.
      safeGoBack();

      if (detectionInFlight) return;
      detectionInFlight = true;
      void (async () => {
        try {
          const result = await detectGoBackResult();
          if (cancelled || exiting) return;
          if (result === 'exhausted' && confirmExit()) {
            exiting = true;
            cancelled = true;
            window.removeEventListener('popstate', handlePopState);
            setIntentionalUtExit(true);
            // Pop the sentinel re-pushed at top of handler, then leave the survey page.
            window.history.go(-2);
          }
        } finally {
          detectionInFlight = false;
        }
      })();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      cancelled = true;
      tabListenerActive = false;
      window.removeEventListener('popstate', handlePopState);
      safeRemoveTabListener();
      // Pop sentinel only after listener removal so back() does not re-arm the trap.
      if (!exiting && isTrapState(window.history.state)) {
        window.history.back();
      }
    };
  }, [hyperbeamInstance, tErrors]);
}

/**
 * Hook to cleanup browser session.
 * Destroys Hyperbeam instance, removes session from backend, and clears atom.
 */
export function useBrowserSessionCleanup(): () => Promise<void> {
  const [removeSessionMutation] = useMutation(REMOVE_HYPERBEAM_SESSION);

  const browserSession = useAtomValue(browserSessionAtom);
  const setBrowserSession = useSetAtom(browserSessionAtom);

  const sessionRef = useRef(browserSession);

  useEffect(() => {
    sessionRef.current = browserSession;
  }, [browserSession]);

  return useCallback(async () => {
    // Get current session from ref to avoid stale closures
    const { instance, sessionId } = sessionRef.current;

    // Clear atom first to prevent race conditions
    setBrowserSession({
      instance: null,
      sessionId: null,
      containerElement: null,
    });

    if (instance) {
      try {
        instance.destroy();
      } catch {
        // Ignore errors during cleanup
      }
    }

    if (sessionId) {
      try {
        await removeSessionMutation({
          variables: { input: { sessionId } },
        });
      } catch {
        // Ignore errors - session may already be closed
      }
    }
  }, [removeSessionMutation, setBrowserSession]);
}
